import { useCallback, useMemo, useState } from 'react';
import { z, ZodIssueCode } from 'zod';
import { createSafeContext, CustomErrorParams, CustomErrorType } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { WithChildren } from '@optimus-web/core';
import { safeGroupParse, SafeParseGrouping } from '../utils/grouping';

interface EmailValidationContextProps {
    invalidEmails: string[];
    setInvalidEmails: (emails: string[]) => void;
}

export const { EmailValidationContextProvider, useEmailValidationContext } = createSafeContext<
    'EmailValidationContext',
    EmailValidationContextProps
>('EmailValidationContext');

export const EmailValidationProvider: React.FC<WithChildren> = ({ children }) => {
    const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
    const value = useMemo(() => ({ invalidEmails, setInvalidEmails }), [invalidEmails, setInvalidEmails]);

    return <EmailValidationContextProvider value={value}>{children}</EmailValidationContextProvider>;
};

interface OnValidationProps {
    parsed: SafeParseGrouping<string>;
    results: string[];
}

const validateListAsync = async ({
    incomingData,
    existingData,
    validateAsync,
    onValidation,
}: {
    incomingData: string[];
    existingData: string[];
    validateAsync: (valid: SafeParseGrouping<string>['valid']) => Promise<string[]>;
    onValidation: ({ parsed, results }: OnValidationProps) => void;
}) => {
    if (!incomingData.length) {
        return false;
    }

    const results = new Set(existingData.filter((item) => incomingData.includes(item)));

    try {
        const parsed = safeGroupParse<string>(incomingData, () => z.string().email());
        const { invalid, valid } = parsed;

        invalid.forEach((email) => results.add(email));

        onValidation({
            parsed,
            results: Array.from(results),
        });

        if (valid.length) {
            const validated = await validateAsync(valid);
            validated?.forEach((item: string) => results.add(item));
        }

        onValidation({
            parsed,
            results: Array.from(results),
        });

        return !results.size;
    } catch (e) {
        return false;
    }
};

export const useAsyncEmailValidation = (
    validateAsync: (params: { emails: Array<string> }) => Promise<{ emails?: Array<string> | null }>
) => {
    const { invalidEmails, setInvalidEmails } = useEmailValidationContext();

    const handleValidation = useCallback(
        ({ ctx, results }: OnValidationProps & { ctx: z.RefinementCtx }) => {
            if (results.length) {
                ctx.addIssue({
                    code: ZodIssueCode.custom,
                    params: {
                        errorType: CustomErrorType.Other,
                        textKey: TextKey.MultipleEmailsValidationWarning,
                        substitutes: { emails: results.join(', ') },
                    } satisfies CustomErrorParams,
                });
            }
            setInvalidEmails(results);
        },
        [setInvalidEmails]
    );

    return useCallback(
        async (data: string[], ctx: z.RefinementCtx) =>
            validateListAsync({
                existingData: invalidEmails,
                incomingData: data,
                validateAsync: async (emails) => validateAsync({ emails }).then((results) => results?.emails || []),
                onValidation: (args) => handleValidation({ ...args, ctx }),
            }),
        [handleValidation, invalidEmails, validateAsync]
    );
};
